import React, { useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import styled from '@emotion/styled'
import {
  Color,
  colors,
  ScreenReaderLiveRegion,
  Theme,
  useTheme,
} from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import { validateEmail } from '@library/api-user'

import { actionTypes } from 'actions/actionTypes'
import { trackUTMAndPromoCode } from 'actions/tracking'
import { checkoutClickContinueToDelivery } from 'actions/trackingKeys'
import { userProspect } from 'actions/user'
import { EmailPasswordForm } from 'routes/Checkout/Components/AboutYou/EmailPasswordForm'
import { ErrorMessage } from 'routes/Checkout/Components/ErrorMessage/ErrorMessage'
import { CheckoutStepIds } from 'routes/Checkout/checkoutConfig'

import { SectionHeader } from '../SectionHeader'
import { SecurePaymentBanner } from './SecurePaymentBanner'
import { ExistingEmailLoginModal } from './components/ExistingEmailLoginModal'
import { ExistingEmailWarningNotification } from './components/ExistingEmailWarningNotification'
import { PaypalTokenErrorNotification } from './components/PaypalTokenErrorNotification'
import { ValidateEmailErrorDisplay } from './components/ValidateEmailErrorDisplay'

type AboutYouProps = {
  onStepChange: (nextStepId: CheckoutStepIds) => void
}

type ThemeProp = {
  citrusTheme: Theme
}

type ValidateEmailStatusType = null | 'error' | 'warning'

const AboutYouContainer = styled.div(({ citrusTheme }: ThemeProp) => {
  const mackerelColor = '#D2D6D9'
  const BreakpointFromMedium = `@media (min-width: ${citrusTheme.tokens.breakpoints[0]})`

  return {
    borderBottom: `1px solid ${mackerelColor}`,
    borderTop: `1px solid ${mackerelColor}`,
    padding: '1rem 0.75rem 1.5rem',
    paddingBottom: '1rem',
    background: colors[Color.White],

    [BreakpointFromMedium]: {
      border: `1px solid ${mackerelColor}`,
      padding: '1.5rem 1.5rem 2rem',
    },
  }
})

export const AboutYou = ({ onStepChange }: AboutYouProps) => {
  const dispatch = useDispatch()
  const { theme } = useTheme()
  const [validateEmailStatus, setValidateEmailStatus] = useState<ValidateEmailStatusType>(null)

  const onSubmit = async (
    email: string,
    _password: string,
    isEmailMarketingEnabled: boolean,
    isCompetitionsAndSurveysEnabled: boolean,
  ) => {
    setValidateEmailStatus(null)
    try {
      const { exists } = await validateEmail({ email: email.trim() })

      if (exists) {
        setValidateEmailStatus('warning')
        datadogRum.addAction('show_validate_email_warning')

        return
      }
    } catch (err) {
      setValidateEmailStatus('error')
      datadogRum.addAction('show_validate_email_error')

      return
    }

    dispatch({
      type: actionTypes.CHECKOUT_SET_EMAIL,
      email,
    })
    dispatch({ type: actionTypes.CHECKOUT_SET_IS_EMAIL_MARKETING_ENABLED, isEmailMarketingEnabled })
    dispatch({
      type: actionTypes.CHECKOUT_SET_IS_COMPETITIONS_AND_SURVEYS_ENABLED,
      isCompetitionsAndSurveysEnabled,
    })
    dispatch(trackUTMAndPromoCode(checkoutClickContinueToDelivery))
    dispatch(userProspect())
    onStepChange(CheckoutStepIds.DELIVERY)
    datadogRum.addAction('signup_checkout_click_continue_to_delivery')
  }

  return (
    <>
      <ExistingEmailLoginModal />
      <AboutYouContainer citrusTheme={theme} data-testing="checkoutAboutYouSection">
        <SectionHeader title="Create account" />
        <ScreenReaderLiveRegion urgency="polite">
          {validateEmailStatus === 'warning' && <ExistingEmailWarningNotification />}
          <PaypalTokenErrorNotification />
        </ScreenReaderLiveRegion>
        <ScreenReaderLiveRegion urgency="polite">
          {validateEmailStatus === 'error' && <ValidateEmailErrorDisplay />}
        </ScreenReaderLiveRegion>
        <EmailPasswordForm onSubmit={onSubmit} />
        <ErrorMessage />
      </AboutYouContainer>
      <SecurePaymentBanner />
    </>
  )
}
